import { gql } from "lib/apolloClient";
import { studentAttendanceFragment } from "./AttendanceSummaryFragments";
import {
  studentNameFragment,
  parentNameFragment,
} from "AttendanceDashboard/modules/AttendanceDashboardFragment";

export const getStudentAttendanceStatisticsQuery = gql`
  query getStudentAttendanceStatistics(
    $studentId: ID!
    $filters: StudentAttendanceFilters
    $overAllPresenceFilter: StudentAttendanceFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        id
        ...studentNameFields
        overallPresence: attendanceV2(filters: $overAllPresenceFilter) {
          presenceOverview {
            totalCount
            presencePercentage
            absencePercentage
            presenceNumber
            absenceNumber
          }
        }
        attendanceV2(filters: $filters) {
          statistics {
            totalCount
            totalDays
            categoryItems {
              ...attendanceStatisticCategoryItem
            }
            courseItems {
              ...attendanceStatisticCourseItem
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${studentAttendanceFragment.attendanceStatisticCategory}
  ${studentAttendanceFragment.attendanceStatisticCourse}
`;

export const getStudentAttendanceStatisticsV2Query = gql`
  query getStudentAttendanceStatisticsV2(
    $studentId: ID!
    $filters: StudentAttendanceFilters
    $overAllPresenceFilter: StudentAttendanceFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        id
        ...studentNameFields
        overallPresence: attendanceV2(filters: $overAllPresenceFilter) {
          presenceOverview {
            totalCount
            presencePercentage
            absencePercentage
            presenceNumber
            absenceNumber
          }
        }
        attendanceV2(filters: $filters) {
          statistics {
            courseItems {
              ...attendanceStatisticCourseItem
            }
          }
          categorySummary {
            percentageItems {
              category {
                id
                label
                color
              }
              percentage
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${studentAttendanceFragment.attendanceStatisticCourse}
`;

export const getStudentAttendanceRecordQuery = gql`
  query getStudentAttendanceRecord(
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $showHomeroomAttendance: Boolean!
    $filters: StudentAttendanceFilters
    $studentAttendanceV2StatsFilters: StudentAttendanceFilters
    $studentAttendanceV2HomeroomStatsFilters: StudentAttendanceFilters
    $studentId: ID!
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        overallStats: attendanceV2(filters: $studentAttendanceV2StatsFilters) {
          edgeInfo {
            totalCount
            categoryFilteredCount
            percentage
          }
        }
        homeroomStats: attendanceV2(
          filters: $studentAttendanceV2HomeroomStatsFilters
        ) @include(if: $showHomeroomAttendance) {
          edgeInfo {
            totalCount
            categoryFilteredCount
            percentage
          }
        }
        attendanceRecord: attendanceV2(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          edges {
            node {
              ...attendanceRecordItem
            }
            cursor
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${studentAttendanceFragment.attendanceRecord}
`;

export const getStudentAttendanceExcusalQuery = gql`
  query getStudentAttendanceExcusal(
    $studentId: ID!
    $filters: AttendanceExcusalFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        id
        yearGroup {
          grade {
            id
            name
          }
        }
        ...studentNameFields
        profileImage
        yearGroup {
          id
          name
        }
        studentAttendanceExcusals(filters: $filters) {
          edges {
            node {
              ...attendanceExcusalItem
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${studentAttendanceFragment.attendanceExcusal}
`;

export const geSingletStudentAttendanceRecordQuery = gql`
  query geSingletStudentAttendanceRecord(
    $id: ID!
    $first: Int
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: StudentAttendanceFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        ...studentNameFields
        profileImage
        courses {
          id
          title
        }
        familyMembers {
          id
          firstName
          middleName
          lastName
          profileImage
        }
        attendanceV2(
          first: $first
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          edges {
            node {
              id
              date
              value {
                id
                label
                color
                abbreviation
                status
                isDefault
                sendSMS
                sendEmail
                attendanceCategory {
                  id
                  color
                }
              }
              remark
              updatedBy {
                id
                firstName
                lastName
                profileImage
              }
              period {
                id
                label
              }
              course {
                id
                title
                learningCourse {
                  id
                  title
                  academicCourse {
                    id
                    label
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
`;

export const getAttendanceExcusalQuery = gql`
  query getAttendanceExcusals(
    $id: ID!
    $isStaffRelation: Boolean!
    $staffRelationFilters: StaffStudentRelationFilter
  ) {
    node(id: $id, type: ATTENDANCE_EXCUSAL) {
      id
      ... on AttendanceExcusal {
        id
        startDate
        endDate
        reason
        durationType
        periods {
          id
          label
          abbreviation
        }
        createdBy {
          id
          ...parentNameFields
        }
        createdAt
        conversation {
          id
          users {
            id
          }
        }
        attachments {
          id
          title
          type
          url
          thumbUrl
          mimeType
          metadata
        }
        attendanceOption {
          id
          label
          color
          attendanceCategory {
            id
            color
          }
        }
        student {
          id
          ...studentNameFields
          profileImage
          courses {
            id
            title
          }
          staffRelation(filters: $staffRelationFilters)
            @include(if: $isStaffRelation) {
            edges {
              node {
                ... on Staff {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
  ${parentNameFragment.parentNameFields}
  ${studentNameFragment.studentNameFields}
`;

export const getStudentOverallPresenceCountQuery = gql`
  query getStudentOverallPresenceCount(
    $studentId: ID!
    $overAllPresenceFilter: StudentAttendanceFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        overallPresence: attendanceV2(filters: $overAllPresenceFilter) {
          presenceOverview {
            totalCount
            presencePercentage
            absencePercentage
            presenceNumber
            absenceNumber
          }
        }
      }
    }
  }
`;

export const getSelectedStudentsExcusalDetailsQuery = gql`
  query getSelectedStudentsExcusalDetails(
    $id: ID!
    $studentsFilters: OrgConnectionFilterInput
    $studentExcusalFilters: AttendanceExcusalFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(filters: $studentsFilters) {
          edges {
            node {
              id
              studentAttendanceExcusals(filters: $studentExcusalFilters) {
                edges {
                  node {
                    id
                    startDate
                    endDate
                    durationType
                    periods {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserPeriodListQueryV2ForAttendance = gql`
  query getUserPeriodListV2(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $periodFilters: PeriodFilterOptions
  ) {
    node(id: $id, type: $type) {
      ... on Student {
        id
        enrolledPeriodsV2(filters: $periodFilters) {
          date
          isSelected
          day
          rotationDay {
            id
            label
          }
          period {
            id
            label
          }
        }
      }
    }
  }
`;
